import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { preferencesAPI } from '@/api/preferences';
import { soundService } from '@/services/soundService';
import { getAuthToken, getPortalType } from '@/utils/auth';

const PreferencesContext = createContext(null);

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

export const PreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch user preferences from backend
   */
  const fetchPreferences = useCallback(async () => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    
    if (!token) {
      // Not authenticated - use defaults
      const defaultPreferences = { sound_effects_enabled: true };
      setPreferences(defaultPreferences);
      soundService.setSoundEnabled(true);
      return defaultPreferences;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await preferencesAPI.getPreferences();
      setPreferences(data);
      // Update sound service
      soundService.setSoundEnabled(data.sound_effects_enabled ?? true);
      return data;
    } catch (err) {
      console.error('[PreferencesContext] Error fetching preferences:', err);
      setError(err.message || 'Failed to fetch preferences');
      // Use defaults on error
      const defaultPreferences = { sound_effects_enabled: true };
      setPreferences(defaultPreferences);
      soundService.setSoundEnabled(true);
      return defaultPreferences;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update user preferences
   */
  const updatePreferences = useCallback(async (updates) => {
    setLoading(true);
    setError(null);

    try {
      const updated = await preferencesAPI.updatePreferences(updates);
      setPreferences(updated);
      // Update sound service
      if ('sound_effects_enabled' in updates) {
        soundService.setSoundEnabled(updates.sound_effects_enabled);
      }
      return updated;
    } catch (err) {
      console.error('[PreferencesContext] Error updating preferences:', err);
      setError(err.message || 'Failed to update preferences');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Play celebration sound (if enabled)
   */
  const playCelebrationSound = useCallback(() => {
    if (preferences?.sound_effects_enabled !== false) {
      soundService.playCelebrationSound();
    }
  }, [preferences]);

  // Fetch preferences on mount
  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        loading,
        error,
        fetchPreferences,
        updatePreferences,
        playCelebrationSound,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

